import React, { useEffect, useRef, useState } from "react";
import Confetti from "react-confetti";

import { Button, Dialog, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { Loader } from "@components/ClinicalNewReportDialog/Loader";

interface ReportCompletionDialogProps {
  open: boolean;
  clientName: string;
  onExportReport: () => void;
  onViewCompletedReport: () => void;
  isGenerating?: boolean;
  isExporting?: boolean;
}

const ConfettiBanner: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative h-full w-full">
      {size.width > 0 && size.height > 0 ? (
        <Confetti
          width={size.width}
          height={size.height}
          numberOfPieces={200}
          gravity={0.05}
          colors={["#9B55DD", "#CC5802", "#6319AB", "#C2C2C2", "#FFA25A"]}
          confettiSource={{ x: 0, y: 0, w: size.width, h: 0 }}
        />
      ) : null}
    </div>
  );
};

export const ReportCompletionDialog: React.FC<ReportCompletionDialogProps> = ({
  open,
  clientName,
  onExportReport,
  onViewCompletedReport,
  isGenerating = false,
  isExporting = false,
}) => {
  const isExportLoading = isGenerating || isExporting;

  return (
    <Dialog
      open={open}
      setOpen={() => {}}
      title={null}
      size="xl"
      showCloseButton={false}
      primaryButton={undefined}
      secondaryButton={undefined}
      bodyClassName="!px-0 !pb-0"
      dialogClassName="!max-w-[36rem] sm:!max-w-[36rem] overflow-hidden"
    >
      <div className="-mt-10 h-[184px] w-full overflow-hidden bg-amethyst-50">
        <ConfettiBanner />
      </div>

      <div className="flex flex-col gap-6 px-8 pb-8 pt-6">
        <div className="flex flex-col gap-4">
          <Headline as="h2" size="large" colorType="primary">
            Nice job finishing your report!
          </Headline>
          <Paragraph size="small" colorType="secondary">
            You&apos;ve completed the report for <span className="ph-no-capture">{clientName}</span>. You can export or
            view the report at any time or from the reports page.
          </Paragraph>
        </div>

        <div className="flex flex-col gap-4">
          <Button
            variant="primary"
            buttonStyle="filled"
            size="lg"
            text={
              <div className="flex items-center justify-center gap-2">
                {isExportLoading ? <Loader className="ml-0 mr-0 h-4 w-4 text-white" /> : null}
                Export report
              </div>
            }
            onClick={onExportReport}
            disabled={isExportLoading}
            className="w-full justify-center"
          />
          <Button
            variant="primary"
            buttonStyle="border"
            size="lg"
            text={
              <div className="flex items-center justify-center gap-2">
                {isExportLoading ? <Loader className="ml-0 mr-0 h-4 w-4" /> : null}
                View report
              </div>
            }
            onClick={onViewCompletedReport}
            disabled={isExportLoading}
            className="w-full justify-center"
          />
        </div>
      </div>
    </Dialog>
  );
};
